import { FC, useContext, useEffect, useRef, useState } from 'react';
import { DataGrid, GridColDef, GridPaginationModel, ptBR } from '@mui/x-data-grid';
import { BsFillTrashFill, BsPencilSquare, BsSearch } from 'react-icons/bs';
import { Form } from 'react-bootstrap';
import { AiFillFileExcel, AiOutlineLink } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { FiUserPlus } from 'react-icons/fi';

import { HiOutlineDocumentReport } from 'react-icons/hi';
import { BsQrCode } from 'react-icons/bs';
import { AiFillPrinter } from 'react-icons/ai';
import IconButton from '@mui/material/IconButton';
import { TbClockPlus } from "react-icons/tb";

import styles from './funcionarios.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalCriarFuncionario from '../../component/Funcionarios/ModalCriarFuncionario';
import ModalImportarPlanilhaExcel from '../../component/Funcionarios/ModalImportarPlanilhaExcel';
import { EventService } from '../../Services/EventService';
import { IEmployee, IFilterEmployee } from '../../Interfaces/interfaces';
import Header from '../../component/Header';
import ModalPrintFuncionario from '../../component/Funcionarios/ModalPrintFuncionario';
import ModalGerarCodeNumber from '../../component/Funcionarios/ModalGerarCodeNumber';

import { MyButton } from '../../component/MyButton';
import { IoMdArrowBack } from 'react-icons/io';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Avatar, Button, Dialog, TextField, Tooltip } from '@mui/material';
import { EnableComponent } from '../../component/EnableComponent';
import { RoleEnum } from '../../models/enumType';
import AuthContext from '../../Context/auth';
import { CompanyService } from '../../Services/CompanyService';
import { Company } from '../../models/eventType';
import { toImageSrc } from '../../Helpers/ImageHelper';
import { useFormat } from '../../hooks/useFormat';
import React from 'react';
import { ModalApontamentoHoras } from '../../component/Funcionarios/ModalApontamentoHoras';
import { DialogLinkFotoFuncionario } from '../../component/Funcionarios/DialogLinkFotoFuncionario';

interface IEvent {
    eventId: number,
    name: string,
    initialDate: Date | undefined,
    finalDate: Date | undefined,
}

const schema = z.object({
    name: z.string().nullable(),
    CPF: z.string().nullable(),
    company: z.string().default(''),
});

type SearchType = z.infer<typeof schema>

type TableActionProps = {
    employee: IEmployee
}

type EmployeeFilter = {
    fullName: string
    cpf: string
    companyId: number
}

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;
const EMPTY_FILTER: EmployeeFilter = { fullName: '', cpf: '', companyId: 0 };

const Funcionarios: FC = () => {

    const {
        register,
        handleSubmit,
        getValues
    } = useForm<SearchType>({
        resolver: zodResolver(schema)
    })

    const { hasRole } = useContext(AuthContext);
    const { formatMaskRgOrCpf } = useFormat();

    //SERVIÇOS EXTERNOS
    const service = new EventService();
    const companyService = new CompanyService();

    const navigate = useNavigate();

    // MANIPULAÇÃO DOS DADOS
    const [funcionarioEdit, setFuncionarioEdit] = useState<IEmployee | undefined>(undefined);
    const [funcionarioPrint, setFuncionarioPrint] = useState<IEmployee | undefined>(undefined);
    const [companys, setCompanys] = useState<Company[]>([]);
    const [evento, setEvento] = useState<IEvent>();


    // LISTAGEM PAGINADA
    const [funcionarios, setFuncionarios] = useState<IEmployee[]>([]);
    const [filter, setFilter] = useState<EmployeeFilter>(EMPTY_FILTER);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const [companyFilter, setCompanyFilter] = useState<string | null>(null);
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
    const lastRequest = useRef(0);

    // Lista completa usada só pelo modal de números de série, que precisa de todos os setores do evento
    const [funcionariosCodeNumber, setFuncionariosCodeNumber] = useState<IEmployee[]>([]);

    // CONTROLE DOS DIALOGS
    const [showPrint, setShowPrint] = useState(false);
    const [showCriarFuncionario, setShowCriarFuncionario] = useState(false);
    const [showModalApontamento, setShowModalApontamentos] = useState(false);
    const [showImportarPlanilha, setShowImportarPlanilha] = useState(false);
    const [showGerarCodeNumbers, setShowGerarCodeNumbers] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fotoVisualizar, setFotoVisualizar] = useState<string | undefined>(undefined);
    const [employeeLink, setEmployeeLink] = useState<IEmployee | undefined>(undefined);

    const { state } = useLocation();
    const eventId: number = state?.eventId;

    // Recarrega a página atual (após cadastrar, editar, importar, excluir...)
    const reloadPage = () => {
        setFuncionarioEdit(undefined);
        setReloadKey(key => key + 1);
    }

    useEffect(() => {
        Promise.all([
            service.getEvent(eventId),
            companyService.getEventCompanysAsync(eventId)
        ]).then(([event, companys]) => {
            setEvento(event);
            setCompanys(companys);
        });

        return () => clearTimeout(searchTimeout.current);
    }, [eventId]);

    useEffect(() => {
        fetchEmployees();
    }, [eventId, filter, paginationModel, reloadKey]);

    const fetchEmployees = async () => {
        const request = ++lastRequest.current;
        setLoading(true);

        const result = await service.getEmployeesPagedAsync({
            eventId: eventId,
            ...filter,
            page: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        });

        // Ignora respostas de buscas que já foram substituídas por outra (troca rápida de página/filtro)
        if (request !== lastRequest.current)
            return;

        // A página atual pode ficar vazia após uma exclusão: volta para a última página com dados
        if (result.items.length === 0 && paginationModel.page > 0 && result.totalPages > 0) {
            setPaginationModel(model => ({ ...model, page: Math.min(model.page - 1, result.totalPages - 1) }));
            return;
        }

        setFuncionarios(result.items);
        setTotalItems(result.totalItems);
        setTotalPages(result.totalPages);
        setLoading(false);
    }

    // Toda mudança de filtro volta para a primeira página
    const applyFilter = (newFilter: EmployeeFilter) => {
        clearTimeout(searchTimeout.current);
        setFilter(newFilter);
        setPaginationModel(model => ({ ...model, page: 0 }));
    }

    const handleFind = (data: SearchType) => {
        applyFilter({
            fullName: data.name?.trim() ?? '',
            cpf: data.CPF?.trim() ?? '',
            companyId: getCompanyId(companyFilter ?? '')
        });
    }

    const scheduleSearch = () => {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => handleFind(getValues()), SEARCH_DEBOUNCE_MS);
    }

    const handleChangeCompany = (companyName: string | null) => {
        setCompanyFilter(companyName);
        applyFilter({
            fullName: getValues('name')?.trim() ?? '',
            cpf: getValues('CPF')?.trim() ?? '',
            companyId: getCompanyId(companyName ?? '')
        });
    }

    const handleOpenGerarCodeNumbers = async () => {
        // Sem page/pageSize a API devolve todos os funcionários do evento
        const filtro: IFilterEmployee = { eventId: eventId, ...EMPTY_FILTER };
        const employees: IEmployee[] = await service.getEmployees(filtro);
        setFuncionariosCodeNumber(employees ?? []);
        setShowGerarCodeNumbers(true);
    }

    const handleDelete = async (row: any) => {
        if (row.employeeId > 0) {
            await service.deleteEmployee(row.employeeId);
            reloadPage();
        }
    }

    const handleEdit = async (row: IEmployee) => {
        if (row.employeeId > 0) {
            var funcionario = await service.getEmployee(row.employeeId);
            setFuncionarioEdit(funcionario);
            setShowCriarFuncionario(true);
        }
    }

    const handlePrint = async (row: IEmployee) => {
        var funcionario = await service.getEmployee(row.employeeId);

        if (funcionario) {
            setFuncionarioPrint(funcionario);
            setShowPrint(true);
        } else {
            toast.error("Não foi possivel localizar o funcionario por favor tente novamente.");
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'photoUri', headerName: 'Foto', headerAlign: 'center', align: 'center',
            renderCell: (params: any) => {
                return (
                    <Avatar
                        alt={params.row.name}
                        src={toImageSrc(params.value)}
                        style={{ cursor: params.value ? 'pointer' : 'default' }}
                        onClick={() => params.value && setFotoVisualizar(toImageSrc(params.value))}
                    />
                )
            }
        },
        // {
        //     field: 'codeNumber',
        //     headerName: 'QRCode',
        //     width: 100
        // },
        {
            field: 'fullName',
            headerName: 'Nome do Funcionario',
            flex: 2,
        },
        {
            field: 'cpf',
            headerName: 'CPF/RG',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return (<span>{formatMaskRgOrCpf(params.value)}</span>)
            },
        },
        {
            field: 'company',
            headerName: 'Fornecedor',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                let company = companys.find(c => c.companyId === params.row.companyId);
                return (<span>{company?.name}</span>)
            },
        },
        {
            field: 'staff',
            headerName: 'Setor',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => <span>{params.value}</span>
        },
        {
            field: 'function',
            headerName: 'Função',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => <span>{params.value}</span>
        },
        {
            field: 'actions',
            headerName: 'Ações',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            minWidth: 260,
            filterable: false,
            renderCell: (params: any) => <TableActionsComponent employee={params.row as IEmployee} />
        }
    ];



    const handleCreate = async () => {
        setFuncionarioEdit(undefined);
        setShowCriarFuncionario(true);
    }

    const getCompanyId = (nameCompany: string): number => {
        if (nameCompany === undefined || nameCompany == null)
            return 0;

        let company = companys
            .find(c => c.name.toLowerCase().trim() === nameCompany.toLowerCase().trim());

        return company?.companyId ?? 0;
    }

    const handleTimeSheet = (employee: IEmployee) => {
        setFuncionarioPrint(employee);
        setShowModalApontamentos(true);
    }

    return (
        <>
            <div className='p-0' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className={styles.panel} >
                    <div className='row m-0 my-3 px-2' style={{ background: 'white', border: '0px', borderRadius: '10px' }}>
                        {/* Chamados como função, e não como <Componente />: por serem declarados dentro da página,
                            como componentes seriam remontados a cada render (perdendo o foco do input e recriando o grid) */}
                        {HeaderComponent()}
                        {FormEmployee()}
                        <div className='d-flex py-2 gap-2' style={{ color: 'white', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            {AcoesComponent()}
                        </div>
                    </div>
                    {TableEmployeeComponent()}
                </div>
            </div>

            <Dialogs />

            {/* Fora de <Dialogs />, que é recriado a cada render e remontaria o dialog (refazendo a liberação) */}
            <DialogLinkFotoFuncionario
                show={employeeLink !== undefined}
                employee={employeeLink}
                eventId={evento?.eventId ?? 0}
                companyName={companys.find(c => c.companyId === employeeLink?.companyId)?.name ?? ''}
                handleClose={() => setEmployeeLink(undefined)}
            />
        </>
    )

    function TableEmployeeComponent() {
        return (
            <React.Fragment>
                <div className="row my-1 mx-2">
                    <div className="flex justify-end px-2 py-1 text-sm" style={{ background: 'white', borderRadius: '10px 10px 0 0' }}>
                        {totalItems > 0
                            ? `Página ${paginationModel.page + 1} de ${totalPages} · ${totalItems} funcionário(s)`
                            : ''}
                    </div>
                    <div style={{ display: 'flex', height: '400px', background: 'white', borderRadius: '0 0 10px 10px' }}>
                        <div style={{ flexGrow: 1 }}>
                            <DataGrid rows={funcionarios}
                                columns={columns}
                                loading={loading}
                                getRowId={(row) => row.employeeId}
                                paginationMode="server"
                                rowCount={totalItems}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[25, 50, 100, 200]}
                                disableColumnFilter
                                localeText={{
                                    ...ptBR.components.MuiDataGrid.defaultProps.localeText,
                                    noRowsLabel: 'Nenhum funcionário encontrado'
                                }}
                                style={{ border: '0px' }} />
                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }



    function TableActionsComponent({ employee }: TableActionProps) {
        return (
            <React.Fragment>
                <EnableComponent role={RoleEnum.RealizarCheckinCheckoutFuncionario}>
                    <Tooltip placement="top" title="Ver Apontamento de Horas">
                        <IconButton style={{ color: '#003958' }} size="large" onClick={() => handleTimeSheet(employee)}>
                            <TbClockPlus />
                        </IconButton>
                    </Tooltip>
                </EnableComponent>

                <EnableComponent role={RoleEnum.RealizarCheckinCheckoutFuncionario}>
                    <Tooltip placement="top" title="Registrar Ponto de Entrada/Saida">
                        <IconButton style={{ color: '#003958' }} size="large" onClick={() => handlePrint(employee)}>
                            <AiFillPrinter />
                        </IconButton>
                    </Tooltip>
                </EnableComponent>

                <EnableComponent role={RoleEnum.CadastrarEditarFuncionario}>
                    <Tooltip placement="top" title="Editar Funcionário">
                        <IconButton style={{ color: '#003958' }} size="large" onClick={() => handleEdit(employee)}>
                            <BsPencilSquare />
                        </IconButton>
                    </Tooltip>
                </EnableComponent>

                <EnableComponent role={RoleEnum.CadastrarEditarFuncionario}>
                    <Tooltip placement="top" title="Gerar link de atualização de foto">
                        <IconButton style={{ color: '#003958' }} size="large" onClick={() => setEmployeeLink(employee)}>
                            <AiOutlineLink />
                        </IconButton>
                    </Tooltip>
                </EnableComponent>

                <EnableComponent role={RoleEnum.ExcluirFuncionario}>
                    <Tooltip placement="top" title="Excluir Funcionário">
                        <IconButton style={{ color: '#003958' }} size="large" onClick={() => handleDelete(employee)}>
                            <BsFillTrashFill />
                        </IconButton>
                    </Tooltip>
                </EnableComponent>
            </React.Fragment>
        )
    }

    function HeaderComponent() {
        return <div className='row my-3'>
            <div className="col-1">
                <MyButton startIcon={<IoMdArrowBack />}
                    onClick={() => navigate('/events')}>
                    VOLTAR
                </MyButton>
            </div>
            <div className="col-10">
                <Header text={`${evento?.name == undefined ? '' : evento?.name}`} />
            </div>
        </div>;
    }

    function FormEmployee() {
        return (
            <React.Fragment>
                <Form onSubmit={handleSubmit(handleFind)} className='row' style={{ border: '0px' }}>
                    <div className="col-md-6 flex justify-center items-center gap-3">
                        <TextField label='Nome do Funcionario'
                            {...register('name', { onChange: scheduleSearch })}
                            className='w-100'
                            disabled={!hasRole(RoleEnum.ConsultarFuncionario)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSubmit(handleFind)();
                                }
                            }} />

                        <TextField label='CPF/RG'
                            className='w-100'
                            {...register('CPF', { onChange: scheduleSearch })}
                            disabled={!hasRole(RoleEnum.ConsultarFuncionario)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSubmit(handleFind)();
                                }
                            }} />

                        <Autocomplete
                            disablePortal
                            className='w-full'
                            value={companyFilter}
                            onChange={(event, value: string | null) => handleChangeCompany(value)}
                            options={companys.map((option: Company) => option.name)}
                            renderInput={(params: any) => <TextField {...params} label="Fornecedores" />}
                        />
                    </div>

                    <div className='col-md-6'>
                        <div className="col-4">
                            <MyButton endIcon={<BsSearch />} type='submit' disabled={!hasRole(RoleEnum.ConsultarFuncionario)}>
                                Pesquisar
                            </MyButton>
                        </div>
                    </div>
                </Form>
            </React.Fragment>
        )
    }

    function Dialogs() {
        return (
            <React.Fragment>
                {evento && (
                    <ModalCriarFuncionario
                        reloadPage={reloadPage}
                        show={showCriarFuncionario}
                        setShow={setShowCriarFuncionario}
                        eventId={evento!.eventId}
                        funcionario={funcionarioEdit}
                        companys={companys}
                    />
                )}

                {funcionarioPrint && <ModalApontamentoHoras
                    employeeId={funcionarioPrint.employeeId}
                    employeeName={funcionarioPrint.fullName}
                    show={showModalApontamento}
                    setShow={setShowModalApontamentos}
                />}

                {evento && (
                    <ModalImportarPlanilhaExcel
                        eventId={evento!.eventId}
                        reloadPage={reloadPage}
                        show={showImportarPlanilha}
                        setShow={setShowImportarPlanilha}
                    />
                )}

                {evento && (
                    <ModalPrintFuncionario
                        funcionario={funcionarioPrint}
                        show={showPrint}
                        setShow={setShowPrint}
                        reloadPage={reloadPage}
                        companys={companys}
                    />
                )}

                {evento && showGerarCodeNumbers && (
                    <ModalGerarCodeNumber
                        funcionarios={funcionariosCodeNumber}
                        show={showGerarCodeNumbers}
                        setShow={setShowGerarCodeNumbers}
                        reloadPage={reloadPage}
                        eventId={evento.eventId}
                    />
                )}

                <Dialog open={!!fotoVisualizar} onClose={() => setFotoVisualizar(undefined)}>
                    <div className="p-3 flex flex-col items-center gap-3">
                        <img src={fotoVisualizar} alt="Foto do funcionário"
                            className="rounded-xl w-full h-auto object-cover max-h-[500px] max-w-[400px]" />
                        <Button variant="contained" onClick={() => setFotoVisualizar(undefined)}>
                            Fechar
                        </Button>
                    </div>
                </Dialog>
            </React.Fragment>
        )
    }

    function AcoesComponent() {
        return (
            <React.Fragment>


                <EnableComponent role={RoleEnum.CadastrarEditarFuncionario}>
                    <MyButton endIcon={<FiUserPlus />}
                        onClick={() => handleCreate()}>
                        Criar Funcionario
                    </MyButton>
                </EnableComponent>

                <EnableComponent role={RoleEnum.CadastrarEditarFuncionario}>
                    <MyButton endIcon={<AiFillFileExcel />}
                        onClick={() => setShowImportarPlanilha(true)}>
                        Importar Funcionários
                    </MyButton>
                </EnableComponent>

                <EnableComponent role={RoleEnum.CadastrarEditarFuncionario}>
                    <MyButton endIcon={<BsQrCode />}
                        onClick={handleOpenGerarCodeNumbers}>
                        Gerar Numeros de Série
                    </MyButton>
                </EnableComponent>

                <MyButton endIcon={<HiOutlineDocumentReport />}
                    onClick={() => navigate(`/reports`, { state: { idEvent: evento?.eventId } })}>
                    Relatório TimeSheet
                </MyButton>
            </React.Fragment>
        )
    }
}

export default Funcionarios;
